import {
  A3innuvaTariffGroupCodeFragment,
  SpanishContractDataDocument,
} from 'generated/resources/graphql'

import createAsyncData from 'modules/core/hooks/createAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useQuery } from 'modules/core/hooks/useQuery'

import { A3innuvaJobPositions } from 'modules/contracts/store/collections/spain/a3innuvaJobPositions'
import { AgreementCodes } from 'modules/contracts/store/collections/spain/agreementCodes'
import { Classifications } from 'modules/contracts/store/collections/spain/classifications'
import { CnoOccupations } from 'modules/contracts/store/collections/spain/cnoOccupations'
import { ContributionTypes } from 'modules/contracts/store/collections/spain/contributionTypes'
import { OccupationCodes } from 'modules/contracts/store/collections/spain/occupationCodes'
import { Regimes } from 'modules/contracts/store/collections/spain/regimes'
import { CompanyInstallations } from 'modules/marketplace/store/collections/companyInstallations'

import { ContractFieldsProps } from '../lib/types'

export interface CotizationGroup {
  value: number
  label: string
}
export interface SpanishContractData {
  loading: boolean
  a3innuvaJobPositions: A3innuvaJobPositions
  agreementCodes: AgreementCodes
  classifications: Classifications
  cnoOccupations: CnoOccupations
  companyInstallations: CompanyInstallations
  contractTypes: ContractFieldsProps[]
  contributionTypes: ContributionTypes
  educationLevels: ContractFieldsProps[]
  occupationCodes: OccupationCodes
  professionalCategories: ContractFieldsProps[]
  regimes: Regimes
  cotizationGroup: A3innuvaTariffGroupCodeFragment[]
  workingDayTypes: ContractFieldsProps[]
}

const useFetchData = createAsyncData(
  (
    legalEntityId: number,
    country: string,
    regimes: Regimes,
    classifications: Classifications,
    cnoOccupations: CnoOccupations,
    contributionTypes: ContributionTypes,
    occupationCodes: OccupationCodes,
    companyInstallations: CompanyInstallations,
    agreementCodes: AgreementCodes,
    a3innuvaJobPositions: A3innuvaJobPositions
  ) => {
    const data = {
      legal_entity_id: legalEntityId,
    }

    if (country === 'es') {
      return Promise.all([
        cnoOccupations.fetch({
          data,
        }),
        contributionTypes.fetch({
          data,
        }),
        regimes.fetch({
          data,
        }),
        classifications.fetch({
          data,
        }),
        occupationCodes.fetchByLegalEntityIds([data.legal_entity_id]),
        companyInstallations.fetch(),
        agreementCodes.fetch({
          data,
        }),
        a3innuvaJobPositions.fetchByLegalEntityIds([data.legal_entity_id]),
      ])
    }

    return Promise.resolve()
  }
)

const useFetchSpanishContractData = (
  legalEntityId: number,
  country: string,
  notHidden?: boolean
): SpanishContractData => {
  const { company } = useCurrentSession()
  const cnoOccupations = useLazyRef<CnoOccupations>(() => new CnoOccupations()).current
  const contributionTypes = useLazyRef<ContributionTypes>(() => new ContributionTypes()).current
  const regimes = useLazyRef<Regimes>(() => new Regimes()).current

  const classifications = useLazyRef<Classifications>(() => new Classifications()).current

  const companyInstallations = useLazyRef(() => new CompanyInstallations()).current
  const occupationCodes = useLazyRef<OccupationCodes>(() => new OccupationCodes()).current
  const agreementCodes = useLazyRef<AgreementCodes>(() => new AgreementCodes()).current
  const a3innuvaJobPositions = useLazyRef<A3innuvaJobPositions>(
    () => new A3innuvaJobPositions()
  ).current

  const { loading: loadingData, data } = useQuery(SpanishContractDataDocument, {
    variables: {
      companyId: company.id,
      legalEntityIds: [legalEntityId],
      notHidden,
    },
    skip: country !== 'es',
  })
  const templateData = data?.contracts.contractTemplatesConnection.nodes[0]
  const professionalCategories = templateData?.spanishProfessionalCategory || []
  const educationLevels = templateData?.spanishEducationLevel || []
  const contractTypes = templateData?.spanishContractType || []
  const workingDayTypes = templateData?.spanishWorkingDayType || []
  const cotizationGroup = data?.payrollIntegrationsA3innuva.tariffGroupsConnection.nodes ?? []

  const loading = useFetchData(
    legalEntityId,
    country,
    regimes,
    classifications,
    cnoOccupations,
    contributionTypes,
    occupationCodes,
    companyInstallations,
    agreementCodes,
    a3innuvaJobPositions
  )

  return {
    loading: loading || loadingData,
    a3innuvaJobPositions,
    agreementCodes,
    classifications,
    cnoOccupations,
    companyInstallations,
    contractTypes,
    contributionTypes,
    educationLevels,
    occupationCodes,
    professionalCategories,
    regimes,
    cotizationGroup,
    workingDayTypes,
  }
}

export default useFetchSpanishContractData
